import React, { useState } from 'react';
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/shared/PageHeader';
import { useJobs } from '../../core/hooks/useJobs';
import { useAuth } from '../../core/contexts/AuthContext';
import { JobDialog } from './components/JobDialog';
import type { Job, JobStatus } from '../../core/models/Job';
import { JobStatus as JobStatusEnum, JobPriority } from '../../core/models/Job';
import { Timestamp } from 'firebase/firestore';
import { Plus } from 'lucide-react';

const statuses: JobStatus[] = [
  JobStatusEnum.DRAFT,
  JobStatusEnum.PENDING,
  JobStatusEnum.IN_PROGRESS,
  JobStatusEnum.ON_HOLD,
  JobStatusEnum.COMPLETED,
];

function KanbanCard({ job }: { job: Job }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: job.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="bg-white border rounded-lg p-4 mb-2 cursor-move hover:shadow-md"
    >
      <h4 className="font-semibold mb-1">{job.title}</h4>
      {job.description && (
        <p className="text-sm text-gray-600 line-clamp-2">{job.description}</p>
      )}
      {job.dueDate && (
        <p className="text-xs text-gray-500 mt-2">
          Due: {new Date(job.dueDate).toLocaleDateString()}
        </p>
      )}
    </div>
  );
}

export const JobsKanbanPage: React.FC = () => {
  const { organizationId } = useAuth();
  const { jobs, updateJob, createJob } = useJobs(organizationId);
  const [localJobs, setLocalJobs] = useState<Job[]>(jobs);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const handleCreateJob = async (data: any) => {
    try {
      if (!organizationId) {
        alert('Client ID is required. Please ensure you are logged in with a valid client context.');
        return;
      }
      if (!data.contractId) {
        alert('Contract is required to create a job.');
        return;
      }
      await createJob({
        ...data,
        organizationId,
        contractId: data.contractId,
        dueDate: data.dueDate ? Timestamp.fromDate(new Date(data.dueDate)) : undefined,
        status: data.status || JobStatusEnum.DRAFT,
        priority: data.priority || JobPriority.MEDIUM,
      });
      setIsCreateDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to create job:', err);
      const errorMessage = err?.message || 'Failed to create job. Please try again.';
      alert(errorMessage);
    }
  };

  React.useEffect(() => {
    setLocalJobs(jobs);
  }, [jobs]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeJob = localJobs.find((j) => j.id === active.id);
    if (!activeJob) return;

    const newStatus = over.id as JobStatus;
    if (activeJob.status === newStatus) return;

    try {
      await updateJob(activeJob.id, { status: newStatus });
      setLocalJobs(
        localJobs.map((job) =>
          job.id === activeJob.id ? { ...job, status: newStatus } : job
        )
      );
    } catch (error) {
      console.error('Error updating job status:', error);
    }
  };

  const jobsByStatus = statuses.reduce((acc, status) => {
    acc[status] = localJobs.filter((job) => job.status === status);
    return acc;
  }, {} as Record<JobStatus, Job[]>);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Jobs Kanban Board"
        description="Drag and drop jobs to change their status"
        icon={Plus}
        actions={
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Job
          </Button>
        }
      />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragEnd={handleDragEnd}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          {statuses.map((status) => (
            <div key={status} className="border rounded-lg p-4 bg-gray-50 min-h-[500px]">
              <h3 className="font-semibold mb-4 capitalize">{status.replace('-', ' ')}</h3>
              <SortableContext
                items={jobsByStatus[status].map((j) => j.id)}
                strategy={verticalListSortingStrategy}
              >
                {jobsByStatus[status].map((job) => (
                  <KanbanCard key={job.id} job={job} />
                ))}
              </SortableContext>
            </div>
          ))}
        </div>
      </DndContext>

      {/* Create Job Dialog */}
      <JobDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={handleCreateJob}
      />
    </div>
  );
};
